import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ScaleIcon from '@mui/icons-material/ScaleOutlined';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeightOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import type { WeightProductionPlanRegisterRow, WeightProductionPendingJobCompletionRow } from '@/types/reports';

/** Weigh & Label TV slide — only in the rotation while ProcessConfiguration.WeightProductionEnabled
 * is on (gated in TvDashboardPage's SLIDES). The headline number is what's still pending job
 * completion — captured on the scale but not yet stock-posted. */
export default function WeightProductionSlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: plans = [] } = useQuery({
    queryKey: ['reports', 'weight-production-plan-register'],
    queryFn: async () => (await apiClient.get<WeightProductionPlanRegisterRow[]>('/reports/weight-production-plan-register')).data,
    refetchInterval: 60_000,
  });
  const { data: pending = [] } = useQuery({
    queryKey: ['reports', 'weight-production-pending-job-completion'],
    queryFn: async () => (await apiClient.get<WeightProductionPendingJobCompletionRow[]>('/reports/weight-production-pending-job-completion')).data,
    refetchInterval: 60_000,
  });

  const totalPlans = useMemo(() => new Set(plans.map((r) => r.orderId)).size, [plans]);
  const producedQty = useMemo(() => plans.reduce((sum, r) => sum + r.producedQty, 0), [plans]);
  const overdueCount = useMemo(() => pending.filter((r) => r.ageInDays >= 1).length, [pending]);

  const plannedVsProduced = useMemo(
    () => groupSumByMonth(plans, (r) => r.plannedStartDate, [
      { key: 'planned', value: (r) => r.plannedQty },
      { key: 'produced', value: (r) => r.producedQty },
    ]),
    [plans],
  );
  const topPending = useMemo(() => topN(pending, (r) => r.ageInDays, 5), [pending]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('weightProduction.totalPlans')} value={totalPlans} icon={<ScaleIcon />} accentColor={colors.weightProduction} />
        <TvKpiTile label={t('weightProduction.producedQty')} value={producedQty.toLocaleString()} icon={<MonitorWeightIcon />} accentColor={colors.production} />
        <TvKpiTile label={t('weightProduction.pendingJobCompletion')} value={pending.length} icon={<PendingActionsIcon />} accentColor={colors.weightProduction} />
        <TvKpiTile label={t('weightProduction.overdueEntries')} value={overdueCount} icon={<WarningAmberIcon />} accentColor={colors.adjustments} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('weightProduction.plannedVsProducedTitle')} subtitle={t('weightProduction.plannedVsProducedSubtitle')} height={420}>
          <DashboardBarChart
            categories={plannedVsProduced.map((m) => m.month)}
            series={[
              { label: tr('columns.plannedQty'), data: plannedVsProduced.map((m) => m.values.planned), color: colors.procurement },
              { label: tr('columns.producedQty'), data: plannedVsProduced.map((m) => m.values.produced), color: colors.weightProduction },
            ]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList
          title={t('weightProduction.pendingByAgeTitle')}
          accentColor={colors.weightProduction}
          items={topPending.map((r) => ({ label: r.barcodeNo, value: r.ageInDays, formattedValue: tr('columns.days', { days: r.ageInDays }) }))}
        />
      </Box>
    </Box>
  );
}
